import React, { useState, useEffect } from 'react';
import styles from "./Settings.module.css"
import SettingsSwitch from '../../components/SettingsSwitch/SettingsSwitch';
import TableRadioButtons from '../../components/TableRadioButtons/TableRadioButtons';
import Timezone from '../../components/Timezone/Timezone';
import useSettingsQuery from '../../hooks/useSettingsQuery';
import useProfileQuery from '../../hooks/useProfileQuery';
import Modal from 'react-modal';
import { useSelector, useDispatch } from 'react-redux';
import { normalizeTableDensity } from '../../config/tableDensity';
import { setDarkMode } from '../../store/actions/graphqlActions';

const Settings = () => {
  const dispatch = useDispatch();
  const isDarkMode = useSelector(state => state.graphql.darkMode);
  const [isOOFMode, setOOFMode] = useState(false);
  const [tableDensityValue, setTableDensity] = useState('');
  const [timeZoneValue, setTimeZone] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasCheckedTimezone, setHasCheckedTimezone] = useState(false);

  const {settingsLoading, settingsData, settingsRefetch, oofLoading, oofData, oofRefetch, updateOOFmode, updateSettings} = useSettingsQuery();
  const {loading, data, refetch} = useProfileQuery();
  const [pageLoading, setLoading] = useState(true);

  const userType = useSelector(state => state.graphql.accessLevel);
  const isApprover = userType && userType.startsWith('Approver');

  // Set the value for Dark Mode from the database
  useEffect(() => {
    //console.log('Settings Data:', settingsData);
    
    if (settingsData && settingsData.getUserProfile && settingsData.getUserProfile.body) {
      const userSettings = settingsData.getUserProfile.body;
      
      // console.log('User Settings from DB:', userSettings);
      // console.log('Dark Mode from DB (raw):', userSettings.darkMode, typeof userSettings.darkMode);
      // console.log('Table Density from DB:', userSettings.tableDensity);
      // console.log('Timezone from DB:', userSettings.timeZone);

      // Dark Mode - handle both boolean and string values
      const darkModeValue = userSettings.darkMode === true || userSettings.darkMode === 'true';
      dispatch(setDarkMode(darkModeValue));
      //console.log('Dark Mode set to (boolean):', darkModeValue, typeof darkModeValue); // DEBUG

      // Table Density - with fallback
      const density = normalizeTableDensity(userSettings.tableDensity || 'comfortable');
      setTableDensity(density);
      localStorage.setItem("tableDensity", density);
      //console.log('Table Density set to:', density); // DEBUG

      // Timezone - handle null/undefined
      const savedTimezone = userSettings.timeZone;
      setTimeZone(savedTimezone || '');
      if (savedTimezone) {
        localStorage.setItem("timeZone", savedTimezone);
      }

      // Only check timezone mismatch once on initial load
      if (!hasCheckedTimezone) {
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timeZoneStatus = localStorage.getItem("timeZoneStatus");

        if (savedTimezone && 
            savedTimezone !== userTimezone && 
            timeZoneStatus !== "doNotUpdate") {
          setIsModalOpen(true);
        }
        
        setHasCheckedTimezone(true);
      }
      
      setLoading(false);
    }
  }, [settingsData, hasCheckedTimezone]);

  const handleDarkModeSwitchChange = async () => {
    const newDarkModeValue = !isDarkMode;
    const previousDarkModeValue = isDarkMode;

    try {
      const changes = { "darkMode": newDarkModeValue };

      // Update the shared theme state IMMEDIATELY for instant feedback
      // (App.js's effect handles the data-theme attribute + localStorage cache).
      dispatch(setDarkMode(newDarkModeValue));

      // Then save to backend using updateSettings
      const result = await updateSettings(changes);
      //console.log('Save result:', result);

    } catch (error) {
      console.error("Error updating Dark Mode:", error);
      // Revert on error
      dispatch(setDarkMode(previousDarkModeValue));
    }
  };

  // Set the value for OOF Mode from the database
  useEffect(() => {
    if (isApprover) {
      if (oofData && oofData.getApprover) {
        const approverOOF = oofData.getApprover.body;
        setOOFMode(approverOOF.outOfOffice);
        localStorage.setItem("outOfOffice", approverOOF.outOfOffice);
      }
    }
  }, [oofData, isApprover]);

  useEffect(() => {
    if (isApprover) {
      localStorage.setItem("outOfOffice", isOOFMode);
    }
  }, [isOOFMode, isApprover]);

  const handleOOFModeSwitchChange = async () => {
    if (isApprover) {
      const newOOFModeValue = !isOOFMode;

      try {
        await updateOOFmode(newOOFModeValue);
        setOOFMode(newOOFModeValue);
        oofRefetch();

      } catch (error) {
        console.error("Error updating Out of Office Mode:", error);
      }
    }
  };

  // Set the value for Table Density from the database
  useEffect(() => {
    if (tableDensityValue) {
      localStorage.setItem("tableDensity", tableDensityValue);
    }
  }, [tableDensityValue]);

  const handleTableDensityChange = async (event) => {
    const newDensityValue = normalizeTableDensity(event.target.value);
    
    //console.log('Changing table density to:', newDensityValue);

    // Update state and localStorage immediately for instant feedback
    setTableDensity(newDensityValue);
    localStorage.setItem("tableDensity", newDensityValue);

    try {
      const changes = { "tableDensity": newDensityValue };
      
      //console.log('Saving to DB - Changes:', changes);

      const result = await updateSettings(changes);
      //console.log('Save result:', result);

    } catch (error) {
      console.error("Error updating Table Density:", error);
      // Revert on error
      const previousDensity = localStorage.getItem("tableDensity");
      setTableDensity(normalizeTableDensity(previousDensity || 'comfortable'));
    }
  };

  const updateTimeZoneSelect = async (event) => {
    const newTimezone = event.target.value;
    try {
      const changes = { "timeZone": newTimezone };
  
      await updateSettings(changes);
      setTimeZone(newTimezone);
      localStorage.setItem("timeZone", newTimezone);
      // Clear the "doNotUpdate" flag when user manually changes timezone
      localStorage.removeItem("timeZoneStatus");

    } catch (error) {
      console.error("Error updating Time Zone:", error);
    }
  };

  const customModalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'var(--theme-bg-elevated)',
        color: 'var(--theme-text)',
    },
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
    },
  };

  const closeModal = () => {
    setIsModalOpen(false);
    localStorage.setItem("timeZoneStatus", "doNotUpdate");
  };

  const syncTimezone = async () => {
    try {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const changes = { "timeZone": userTimezone };
  
      await updateSettings(changes); 
      setTimeZone(userTimezone);
      localStorage.setItem("timeZone", userTimezone);
      localStorage.removeItem("timeZoneStatus");
      setIsModalOpen(false);
      
    } catch (error) {
      console.error("Error updating Time Zone:", error);
      setIsModalOpen(false);
    }
  };

  if (!pageLoading)
  return (
    <div>
      <div className={styles.container}>
        <h2 className={styles.title}>Settings</h2>
        <div className={styles.items}>
          <table className={styles.itemTable}>
            <tbody>
            <tr>
              <th className={styles.labeltxt}>Dark Mode</th>
              <th className={styles.control}>
                {}
                <SettingsSwitch handleChange={handleDarkModeSwitchChange} toggleData={isDarkMode}/>
              </th>
            </tr>

            {isApprover ? <tr>
              <th className={styles.labeltxt}>Out of Office Mode</th>
              <th className={styles.control}><SettingsSwitch handleChange={handleOOFModeSwitchChange} toggleData={isOOFMode}/></th>
            </tr> : null}

            <tr>
              <th className={styles.labeltxt}>Table Density</th>
              <th className={styles.control}><TableRadioButtons handleDensityChange={handleTableDensityChange} tableDensityValue={tableDensityValue}/></th>
            </tr>

            <tr>
              <th className={styles.labeltxt}>Time Zone</th>
              <th className={styles.control}><Timezone handleTimezoneChange={updateTimeZoneSelect} timeZoneValue={timeZoneValue}/></th>
            </tr>
            </tbody>
          </table>

        </div>
      </div>
      <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          style={customModalStyles}
          contentLabel="Timezone Conflict"
      >
          <button className={styles.closeButton} onClick={closeModal}>
              <span className={styles.closeIcon}>x</span>
          </button>
          <h2 className={styles.timeZoneMessage}> There is a mismatch between your system timezone and your current location.</h2>
          <h2 className={styles.timeZoneQuestion}> Would you like to update your time zone?</h2>
          <div className={styles.modalButtonsContainer}> 
            <button className={styles.yesButton} onClick={syncTimezone}> Yes </button>
            <button className={styles.noButton} onClick={closeModal}> No </button>
          </div>

      </Modal>
    </div>
    
  );
};

export default Settings;
